import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { type SupportedLanguage } from "@/lib/i18n";
import { siteUrl } from "@/lib/siteConfig";
import { generateLocalizedUrl } from "@/lib/slugUtils";
import { trackSEOEvents, validateSchema } from "@/lib/seoAnalytics";
interface StructuredDataProps {
  page: 'home' | 'about' | 'services' | 'portfolio' | 'contact' | 'price-form' | 'jotform-expert' | 'fillout-expert' | 'industry-real-estate' | 'industry-ecommerce' | 'industry-restaurants' | 'industry-financial' | 'industry-agencies';
  currentLanguage: SupportedLanguage;
}

const StructuredData = ({ page, currentLanguage }: StructuredDataProps) => {
  const { t } = useTranslation(['seo', 'common']);
  
  const baseUrl = siteUrl;
  
  // Organization schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": t('common:author'),
    "url": baseUrl,
    "logo": `${baseUrl}/favicon.ico`,
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "info@priceforms.com",
      "contactType": "customer service",
      "availableLanguage": ["en", "cs", "sk", "da"]
    },
    "availableLanguage": ["en", "cs", "sk", "da"],
    "areaServed": [
      "Czech Republic",
      "Slovakia",
      "Denmark"
    ],
    "sameAs": [
      "https://linkedin.com/in/danielgadus",
      "https://github.com/danielgadus"
    ]
  };

  // Enhanced service schema for services page
  const serviceSchema = page === 'services' ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Custom Price Calculators & Smart Forms",
    "description": "Professional no-code solutions including custom price calculators, smart forms, and business automation tools. Tailored for craftsmen, contractors, and service businesses.",
    "provider": {
      "@type": "Organization",
      "name": t('common:author'),
      "url": baseUrl,
      "logo": `${baseUrl}/favicon.ico`
    },
    "serviceType": "No-Code Business Automation",
    "category": "Business Software",
    "audience": {
      "@type": "Audience",
      "audienceType": "Small to Medium Businesses"
    },
    "availableLanguage": ["en", "cs", "sk", "da"],
    "areaServed": [
      "Czech Republic",
      "Slovakia",
      "Denmark"
    ],
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "priceRange": "€500-€5000"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "No-Code Solutions",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service", 
            "name": "Custom Price Calculators"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Smart Forms & Workflows"
          }
        }
      ]
    }
  } : null;

  // Local business schema for contact page
  const localBusinessSchema = page === 'contact' ? {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": t('common:author'),
    "email": "info@priceforms.com",
    "areaServed": ["Czech Republic", "Slovakia", "Denmark"],
    "availableLanguage": ["en", "cs", "sk", "da"],
    "openingHours": "Mo-Fr 09:00-18:00"
  } : null;

  // Person schema for about page
  const personSchema = page === 'about' ? {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": t('common:author'),
    "jobTitle": "No-Code Solutions Expert",
    "description": t('about.description'),
    "url": `${baseUrl}${generateLocalizedUrl('about', currentLanguage)}`,
    "sameAs": [
      "https://linkedin.com/in/danielgadus",
      "https://github.com/danielgadus"
    ]
  } : null;

  // Website schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": t(`seo:${page}.title`),
    "url": baseUrl
  };

  // BreadcrumbList schema for all pages except home
  const breadcrumbSchema = page !== 'home' ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": t('common:navigation.home'),
        "item": `${baseUrl}/${currentLanguage}`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": t(`common:navigation.${page}`),
        "item": `${baseUrl}${generateLocalizedUrl(page, currentLanguage)}`
      }
    ]
  } : null;

  const schemas = [organizationSchema, websiteSchema];
  if (serviceSchema) schemas.push(serviceSchema as any);
  if (localBusinessSchema) schemas.push(localBusinessSchema as any);
  if (personSchema) schemas.push(personSchema as any);
  if (breadcrumbSchema) schemas.push(breadcrumbSchema as any);

  // Analytics: track when structured data is injected
  useEffect(() => {
    try {
      schemas.forEach((schema) => {
        if (validateSchema(schema)) {
          const type = (schema as any)['@type'] || 'Unknown';
          trackSEOEvents.structuredDataLoaded(String(type));
        }
      });
    } catch {
      // noop
    }
  }, [page, currentLanguage]);
  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default StructuredData;